import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { EsignModal, Font } from '@apply/ui';
import * as Store from '@apply/store';
import { Header } from '../Header';
import { useAuthentication } from '../LoginPage';
import { alertModal, loginAlertModal } from '@apply/ui';

export interface HomePageProps {
    actions?: Actions;
}

export interface Actions {
    onUpdateEsign?: (esign: Store.BorrowerStore.Types.Esign) => void;
    onNeedIpAddress?: () => Promise<string>;
}

const getElectronicRecordsHtml = async () => await Store.CrossStore.getElectronicRecordsHtml();
const saveEsignDetails = async (userId: string, userAgreementTypeId: number, fontId: number, ipAddress: string) =>
    await Store.CrossStore.signUserAgreement(userId, userAgreementTypeId, fontId, ipAddress);

export const ResignAgreement: React.FC<HomePageProps> = ({ actions }) => {
    const { isRegisteredUser, authentication, isAuthenticated, logout } = useAuthentication();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>('');
    const [ipAddress, setIpAddress] = useState<string>('');
    const [esignHtml, set_esignHtml] = useState<string>('');
    const [agreementOpen, setAgreementOpen] = useState<boolean>(false);
    const [eSignFontId, setESignFontId] = useState<string>('');
    const [agreeToEsignAgreement, setAgreeToEsignAgreement] = useState<boolean>(false);
    const [userName, set_userName] = useState<string>('');

    const navigate = useNavigate();
    const thisLocation = useLocation();

    useEffect(() => {
        if (!authentication) {
            return;
        }

        if (!isAuthenticated || !isRegisteredUser) {
            window.location.href = '/login';
            return;
        }
        set_userName(`${authentication.user.firstName} ${authentication.user.lastName}`);
    }, [authentication, isAuthenticated, isRegisteredUser]);

    useEffect(() => {
        let canceled = false;

        if (actions?.onNeedIpAddress) {
            actions.onNeedIpAddress().then((ip) => {
                if (!canceled) {
                    setIpAddress(ip);
                }
            });
        }

        return () => {
            canceled = true;
        };
    }, [actions]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${thisLocation.pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, thisLocation.pathname]);

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const noop = () => {};

    const onConfirmAgreement = (font: Font) => {
        setAgreementOpen(false);
        setESignFontId(font.id);
        setAgreeToEsignAgreement(true);
    };

    const onRejectAgreement = () => {
        setAgreementOpen(false);
        setAgreeToEsignAgreement(false);
    };

    const onClickESignAgreement = async () => {
        const html = await getElectronicRecordsHtml();
        set_esignHtml(html);
        setAgreementOpen(true);
    };

    const onSave = async () => {
        try {
            setLoading(true);

            const user = authentication?.user;
            await saveEsignDetails(user.userId, 0, parseInt(eSignFontId), ipAddress);
            window.location.href = '/home';
        } catch {
            setLoading(false);
        }
    };

    return (
        <>
            <div className='app-container home-page-container'>
                <Header />
                <main>
                    <section className='completed-applications'>
                        <div
                            className='row'
                            style={{ marginBottom: '0' }}
                        >
                            <div className='columns small-12 large-12'>
                                Our User Agreement has changed.{' '}
                                <b>In order to continue to use this website you must agree to the new terms.</b>
                                <br />
                                <br /> Please click the consent checkbox below and sign the agreement.
                                <br />
                                <br />
                            </div>
                        </div>
                        <div
                            className='columns small-12 large-12'
                            style={{ display: 'flex', flexDirection: 'row' }}
                        >
                            <div>
                                <input
                                    id='agreeToEsignAgreement'
                                    data-testid='register-page-agree-to-esign-agreement'
                                    type='checkbox'
                                    checked={agreeToEsignAgreement}
                                    required
                                    // need a no-op onChange because otherwise React gripes a bunch in the console
                                    onChange={noop}
                                    onClick={onClickESignAgreement}
                                    disabled={!userName || loading}
                                />
                            </div>
                            <div style={{ marginLeft: '10px' }}>
                                <label
                                    htmlFor='agreeToEsignAgreement'
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Consent to Use Electronic Records and Disclosures. By checking this check box and clicking
                                    "Save", I acknowledge that I have read and agree on behalf of the Business Entities to the
                                    Consent to Use Electronic Records and Disclosures.
                                </label>
                            </div>
                        </div>
                        <div className='columns small-12 large-12'>
                            <button
                                type='button'
                                className='button signin-button'
                                disabled={!agreeToEsignAgreement || loading}
                                onClick={onSave}
                            >
                                Save
                            </button>
                        </div>
                    </section>
                </main>
            </div>
            <EsignModal
                agreementHtml={esignHtml}
                isOpen={agreementOpen}
                signatureText={userName}
                cancelCallback={onRejectAgreement}
                confirmCallback={(font) => onConfirmAgreement(font)}
                ipAddress={ipAddress}
                rejectLabel='I DO NOT ACCEPT THIS AGREEMENT'
                acceptLabel='I ACCEPT THIS AGREEMENT'
            />
        </>
    );
};
// eslint-disable-next-line
export const mapStateToProps = (state: Store.ApplyRootStateType) => ({});

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            onUpdateEsign: (esign: Store.BorrowerStore.Types.Esign) => dispatch(Store.BorrowerStore.Actions.updateEsign(esign)),
            onNeedIpAddress: () => dispatch(Store.CrossStore.getIpAddress()),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(ResignAgreement);
